import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, since } from '../lib/format.ts';
import { shrinkPhoto } from '../lib/images.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi } from './Reports.tsx';

/**
 * Social: one planner for the business's posts. A post is written once, for several accounts;
 * Facebook and Instagram publish by themselves at the planned time, LinkedIn and X wait under
 * "Post by hand" with the text ready to copy. With approval on, a manager looks first.
 */

type Tab = 'planner' | 'calendar' | 'accounts' | 'settings';
type Channel = 'facebook' | 'instagram' | 'linkedin' | 'x';
type PState = 'draft' | 'waiting' | 'scheduled' | 'publishing' | 'published' | 'partly' | 'failed' | 'cancelled';
type TState = 'pending' | 'due' | 'publishing' | 'published' | 'failed' | 'skipped';
const CHANNELS: Channel[] = ['facebook', 'instagram', 'linkedin', 'x'];
const API: Channel[] = ['facebook', 'instagram'];
const LIMITS: Record<Channel, number> = { facebook: 63_206, instagram: 2_200, linkedin: 3_000, x: 280 };
const X_LINK = 23;
const IG_TAGS = 30;

interface Account {
  id: string;
  channel: Channel;
  name: string;
  handle: string | null;
  external_id: string | null;
  mode: 'api' | 'manual';
  has_token: boolean;
  token_readable: boolean;
  problem: string | null;
  verified_at: string | null;
  verified_name: string | null;
  last_error: string | null;
  active: boolean;
  published?: number;
  last_post?: string | null;
}
interface PostRow {
  id: string;
  number: string;
  body: string;
  excerpt: string;
  image: string | null;
  link: string | null;
  state: PState;
  scheduled_at: string | null;
  published_at: string | null;
  when: string | null;
  author_name: string | null;
  note: string | null;
  last_error: string | null;
  targets: { id: string; account: string; account_name: string; channel: Channel; state: TState; url: string | null }[];
  likes: number;
  comments: number;
  shares: number;
  due: number;
  engagement?: number;
}
interface Due {
  id: string;
  post: string;
  number: string;
  state: TState;
  channel: Channel;
  account_name: string;
  handle: string | null;
  scheduled_at: string | null;
  error: string | null;
  text: string;
  image: string | null;
}
interface Overview {
  counts: { week: number; waiting: number; drafts: number; failed: number; published: number };
  month: { likes: number; comments: number; shares: number };
  channels: { channel: Channel; posts: number; likes: number; comments: number; shares: number }[];
  upcoming: PostRow[];
  top: PostRow[];
  due: Due[];
  accounts: Account[];
}
interface Target {
  id: string;
  account: string;
  channel: Channel;
  account_name: string;
  handle: string | null;
  mode: 'api' | 'manual';
  state: TState;
  own_text: boolean;
  body: string | null;
  text: string;
  copy: string;
  url: string | null;
  error: string | null;
  published_at: string | null;
  posted_by_name: string | null;
  remote_id: string | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  stats_at: string | null;
  length: { length: number; limit: number; over: number; tags: number };
}
interface Post {
  id: string;
  number: string;
  body: string;
  image: string | null;
  picture: { id: string; url: string; width: number | null; height: number | null } | null;
  link: string | null;
  state: PState;
  scheduled_at: string | null;
  published_at: string | null;
  author_name: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  note: string | null;
  last_error: string | null;
  targets: Target[];
}
interface Setup {
  accounts: Account[];
  approval: boolean;
  can_publish: boolean;
  site_url: string | null;
}
interface Settings {
  approval: boolean;
  site_url: string | null;
  can_keep_token: boolean;
}

const TABS: { key: Tab; icon: IconName; perm?: string }[] = [
  { key: 'planner', icon: 'list' },
  { key: 'calendar', icon: 'calendar' },
  { key: 'accounts', icon: 'users' },
  { key: 'settings', icon: 'settings', perm: 'social.settings' },
];
const STATE: Record<PState, { tone: Tone; icon: IconName }> = {
  draft: { tone: 'neutral', icon: 'edit' },
  waiting: { tone: 'warn', icon: 'clock' },
  scheduled: { tone: 'info', icon: 'calendar' },
  publishing: { tone: 'primary', icon: 'send' },
  published: { tone: 'ok', icon: 'checkCircle' },
  partly: { tone: 'warn', icon: 'alert' },
  failed: { tone: 'danger', icon: 'alert' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const T_ICON: Record<TState, IconName> = { pending: 'clock', due: 'edit', publishing: 'send', published: 'checkCircle', failed: 'alert', skipped: 'minus' };
const SHORT: Record<Channel, string> = { facebook: 'f', instagram: 'ig', linkedin: 'in', x: 'X' };

// ---- length, the same rules as the server --------------------------------------------------------

const URLS = /https?:\/\/[^\s]+/gi;
const letters = (s: string) => [...s].length;
const copyText = (ch: Channel, text: string, link: string | null) => (link && ch !== 'instagram' && !text.includes(link) ? `${text}\n\n${link}`.trim() : text);
function measure(ch: Channel, text: string, link: string | null) {
  let length: number;
  if (ch === 'x') {
    const full = copyText('x', text, link);
    length = letters(full.replace(URLS, '')) + (full.match(URLS)?.length ?? 0) * X_LINK;
  } else length = letters(ch === 'facebook' ? text : copyText(ch, text, link));
  const tags = (text.match(/(^|\s)#[\p{L}\p{N}_]+/gu) ?? []).length;
  return { length, limit: LIMITS[ch], over: Math.max(0, length - LIMITS[ch]), tags };
}

const pad = (n: number) => String(n).padStart(2, '0');
const toLocalInput = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeOf = (iso: string) => new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const n0 = (n: number) => new Intl.NumberFormat().format(n);
/** Facebook, Instagram, LinkedIn, X, then by name. */
const byChannel = <A extends { channel: Channel; name: string }>(list: A[]) => [...list].sort((a, b) => CHANNELS.indexOf(a.channel) - CHANNELS.indexOf(b.channel) || a.name.localeCompare(b.name));
/** The next full hour, for a new plan. */
const nextHour = () => {
  const d = new Date(Date.now() + 3600_000);
  d.setMinutes(0, 0, 0);
  return d.toISOString();
};

export function ChannelMark({ channel, size = 'md' }: { channel: Channel; size?: 'sm' | 'md' }) {
  return (
    <span className={`soc-mark soc-${channel} soc-mark-${size}`} title={t(`social.ch_${channel}` as MessageKey)} aria-label={t(`social.ch_${channel}` as MessageKey)}>
      {SHORT[channel]}
    </span>
  );
}
function PostBadge({ state }: { state: PState }) {
  const s = STATE[state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`social.st_${state}` as MessageKey)} />;
}
function TargetState({ state }: { state: TState }) {
  return (
    <span className={`soc-tstate soc-t-${state}`}>
      <Icon name={T_ICON[state]} size={13} />
      {t(`social.ts_${state}` as MessageKey)}
    </span>
  );
}

export function Social() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.perm || can(x.perm));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'planner') as Tab;
  const { data: o, reload } = useAction<Overview>('social.overview');
  const [version, setVersion] = useState(0);
  const [compose, setCompose] = useState<{ id: string | null; at?: string | null } | null>(null);
  const open = params.get('p');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) v === null ? p.delete(k) : p.set(k, v);
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  useEffect(() => subscribe(['social'], () => void changed()), [reload]);
  const manage = can('social.manage');
  const needs = o ? o.due.length + (can('social.publish') ? o.counts.waiting : 0) : 0;
  const engagement = o ? o.month.likes + o.month.comments + o.month.shares : 0;

  return (
    <Shell>
      <div className={`page reports soc-page tab-${tab}`}>
        <div className="page-head">
          <div>
            <h1>{t('social.title')}</h1>
            <p className="muted">{t('social.subtitle')}</p>
          </div>
          <div className="page-actions">
            {manage ? (
              <Button variant="primary" icon="plus" onClick={() => setCompose({ id: null })} disabled={!!o && !o.accounts.some((a) => a.active)}>
                {t('social.new_post')}
              </Button>
            ) : null}
          </div>
        </div>
        {o && !o.accounts.some((a) => a.active) ? (
          <p className="callout callout-info">
            <Icon name="info" size={16} />
            <span>
              {t('social.no_accounts')}{' '}
              {can('social.settings') ? (
                <button className="link" onClick={() => set({ tab: 'accounts', p: null })}>
                  {t('social.add_first')}
                </button>
              ) : (
                t('social.ask_manager')
              )}
            </span>
          </p>
        ) : o?.accounts.some((a) => a.active && a.last_error) ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>
              {t('social.account_error', {
                names: o.accounts
                  .filter((a) => a.active && a.last_error)
                  .map((a) => a.name)
                  .join(', '),
              })}{' '}
              {can('social.settings') ? (
                <button className="link" onClick={() => set({ tab: 'accounts', p: null })}>
                  {t('social.reconnect')}
                </button>
              ) : null}
            </span>
          </p>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('social.at_a_glance')}>
            <Kpi label={t('social.k_week')} value={String(o.counts.week)} hint={o.counts.drafts ? tn('social.k_drafts', o.counts.drafts) : t('social.k_no_drafts')} />
            <Kpi label={t('social.k_published')} value={String(o.counts.published)} hint={t('social.k_last_30')} />
            <Kpi
              label={t('social.k_engagement')}
              value={n0(engagement)}
              hint={t('social.k_engagement_hint', { likes: n0(o.month.likes), comments: n0(o.month.comments), shares: n0(o.month.shares) })}
            />
            <Kpi
              label={t('social.k_needs')}
              value={String(needs + o.counts.failed)}
              hint={needs + o.counts.failed ? t('social.k_needs_hint') : t('social.k_all_done')}
              warn={needs + o.counts.failed > 0}
            />
          </section>
        )}
        <div className="segmented tabs soc-tabs" role="tablist" aria-label={t('social.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'planner' ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`social.tab_${x.key}` as MessageKey)}
              {x.key === 'planner' && needs ? <span className="count">{needs}</span> : null}
            </button>
          ))}
        </div>
        {!o ? null : tab === 'planner' ? (
          <PlannerTab key={version} o={o} params={params} set={set} onOpen={(id) => set({ p: id })} onChange={changed} />
        ) : tab === 'calendar' ? (
          <CalendarTab key={version} params={params} set={set} onOpen={(id) => set({ p: id })} onNew={manage ? (at) => setCompose({ id: null, at }) : undefined} />
        ) : tab === 'accounts' ? (
          <AccountsTab key={version} onChange={changed} />
        ) : (
          <SettingsTab onChange={changed} />
        )}
      </div>
      {open ? <PostDialog key={`${open}-${version}`} id={open} onClose={() => set({ p: null })} onChange={changed} onEdit={(id) => setCompose({ id })} onOpen={(id) => set({ p: id })} /> : null}
      {compose ? (
        <Composer
          id={compose.id}
          at={compose.at ?? null}
          onClose={() => setCompose(null)}
          onDone={async (id) => {
            setCompose(null);
            await changed();
            set({ p: id });
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- planner -------------------------------------------------------------------------------------

const FILTERS = ['open', 'done', 'all'] as const;

function PlannerTab({
  o,
  params,
  set,
  onOpen,
  onChange,
}: {
  o: Overview;
  params: URLSearchParams;
  set: (n: Record<string, string | null>) => void;
  onOpen: (id: string) => void;
  onChange: () => Promise<void>;
}) {
  const f = (FILTERS as readonly string[]).includes(params.get('f') ?? '') ? (params.get('f') as (typeof FILTERS)[number]) : 'open';
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);
  const { data } = useAction<PostRow[]>('social.posts', { state: f, q: query || null });
  const waiting = (data ?? []).filter((p) => p.state === 'waiting');
  const rest = (data ?? []).filter((p) => p.state !== 'waiting');
  return (
    <div className="soc-planner">
      <div className="soc-main">
        {o.due.length ? <DueList due={o.due} onOpen={onOpen} onChange={onChange} /> : null}
        <section className="panel">
          <div className="soc-list-head">
            <div className="segmented" role="group" aria-label={t('social.show')}>
              {FILTERS.map((x) => (
                <button key={x} className={f === x ? 'active' : ''} aria-pressed={f === x} onClick={() => set({ f: x === 'open' ? null : x })}>
                  {t(`social.f_${x}` as MessageKey)}
                </button>
              ))}
            </div>
            <label className="soc-search">
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} placeholder={t('social.search')} aria-label={t('social.search')} onChange={(e) => setQ(e.target.value)} />
            </label>
          </div>
          {!data ? (
            <Skeleton h={240} />
          ) : !data.length ? (
            <EmptyState
              icon="share"
              title={query ? t('social.nothing_found') : f === 'open' ? t('social.nothing_planned') : t('social.nothing_yet')}
              body={f === 'open' && !query ? t('social.nothing_planned_hint') : undefined}
            />
          ) : (
            <>
              {waiting.length ? (
                <>
                  <h3 className="soc-group">{tn('social.waiting_group', waiting.length)}</h3>
                  <PostList rows={waiting} onOpen={onOpen} />
                </>
              ) : null}
              {rest.length ? (
                <>
                  {waiting.length ? <h3 className="soc-group">{t('social.other_posts')}</h3> : null}
                  <PostList rows={rest} onOpen={onOpen} />
                </>
              ) : null}
            </>
          )}
        </section>
      </div>
      <aside className="soc-side">
        <section className="panel">
          <h2 className="rep-sub">{t('social.by_network')}</h2>
          <ul className="soc-net">
            {o.channels.map((c) => (
              <li key={c.channel}>
                <ChannelMark channel={c.channel} />
                <span className="vat-party">
                  <strong>{t(`social.ch_${c.channel}` as MessageKey)}</strong>
                  <span className="muted small">{tn('social.n_posts', c.posts)}</span>
                </span>
                <span className="soc-net-num tabular" title={t('social.engagement_title')}>
                  {n0(c.likes + c.comments + c.shares)}
                </span>
              </li>
            ))}
          </ul>
          <p className="muted small">{t('social.by_network_hint')}</p>
        </section>
        {o.top.length ? (
          <section className="panel">
            <h2 className="rep-sub">{t('social.top')}</h2>
            <ol className="soc-top">
              {o.top.map((p) => (
                <li key={p.id}>
                  <button className="soc-top-row" onClick={() => onOpen(p.id)}>
                    {p.image ? <img src={p.image} alt="" loading="lazy" /> : <span className="soc-thumb-empty" aria-hidden="true" />}
                    <span className="soc-top-text">{p.excerpt}</span>
                    <span className="soc-top-num tabular">{n0(p.engagement ?? 0)}</span>
                  </button>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </aside>
    </div>
  );
}

function PostList({ rows, onOpen }: { rows: PostRow[]; onOpen: (id: string) => void }) {
  return (
    <ul className="soc-posts">
      {rows.map((p) => {
        const stats = p.likes + p.comments + p.shares;
        return (
          <li key={p.id}>
            <button className="soc-post-row" onClick={() => onOpen(p.id)}>
              {p.image ? <img className="soc-thumb" src={p.image} alt="" loading="lazy" /> : <span className="soc-thumb soc-thumb-empty" aria-hidden="true" />}
              <span className="soc-post-text">
                <span className="soc-post-excerpt">{p.excerpt || <span className="muted">{t('social.picture_only')}</span>}</span>
                <span className="soc-post-meta">
                  <span className="soc-marks">
                    {p.targets.map((x) => (
                      <span key={x.id} className={`soc-dot-wrap soc-t-${x.state}`} title={`${x.account_name}: ${t(`social.ts_${x.state}` as MessageKey)}`}>
                        <ChannelMark channel={x.channel} size="sm" />
                      </span>
                    ))}
                  </span>
                  <span className="muted small">
                    {p.when ? (p.published_at ? dateTime(p.published_at) : p.state === 'draft' ? t('social.planned_for', { at: dateTime(p.when) }) : dateTime(p.when)) : t('social.no_time')}
                  </span>
                  {stats ? (
                    <span className="soc-stats muted small">
                      <Icon name="star" size={13} /> {n0(p.likes)} <Icon name="chat" size={13} /> {n0(p.comments)} <Icon name="share" size={13} /> {n0(p.shares)}
                    </span>
                  ) : null}
                </span>
              </span>
              <span className="soc-post-state">
                <PostBadge state={p.state} />
                {p.due ? <span className="muted small">{tn('social.n_by_hand', p.due)}</span> : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** The posts LinkedIn and X wait for: copy the text, post it there, then mark it posted. */
function DueList({ due, onOpen, onChange }: { due: Due[]; onOpen: (id: string) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const [marking, setMarking] = useState<Due | null>(null);
  return (
    <section className="panel soc-due" aria-labelledby="soc-due-title">
      <div>
        <h2 className="rep-sub" id="soc-due-title">
          <Icon name="edit" size={18} /> {t('social.by_hand')}
        </h2>
        <p className="muted small">{t('social.by_hand_hint')}</p>
      </div>
      <ul className="soc-due-list">
        {due.map((d) => (
          <li key={d.id} className={d.state === 'failed' ? 'failed' : ''}>
            <div className="soc-due-head">
              <ChannelMark channel={d.channel} />
              <span className="vat-party">
                <strong>{d.account_name}</strong>
                <span className="muted small">
                  {[d.handle, d.scheduled_at ? since(d.scheduled_at) : null].filter(Boolean).join(' · ')}
                  {' · '}
                  <button className="link" onClick={() => onOpen(d.post)}>
                    {d.number}
                  </button>
                </span>
              </span>
            </div>
            {d.state === 'failed' ? (
              <p className="soc-note warn small">
                <Icon name="alert" size={14} /> {t('social.failed_by_hand', { error: d.error ?? '' })}
              </p>
            ) : null}
            <div className="soc-due-body">
              {d.image ? <img src={d.image} alt="" loading="lazy" /> : null}
              <p>{d.text}</p>
            </div>
            {can('social.manage') ? <HandActions channel={d.channel} text={d.text} image={d.image} onMark={() => setMarking(d)} /> : null}
          </li>
        ))}
      </ul>
      {marking ? (
        <MarkPosted
          target={marking.id}
          channel={marking.channel}
          onClose={() => setMarking(null)}
          onDone={async () => {
            setMarking(null);
            await onChange();
          }}
        />
      ) : null}
    </section>
  );
}

const OPEN_URL: Record<Channel, (text: string) => string> = {
  facebook: () => 'https://www.facebook.com/',
  instagram: () => 'https://www.instagram.com/',
  linkedin: () => 'https://www.linkedin.com/feed/',
  // X's web intent fills the text in.
  x: (text) => `https://x.com/intent/tweet?text=${encodeURIComponent(text)}`,
};

function HandActions({ channel, text, image, onMark }: { channel: Channel; text: string; image: string | null; onMark: () => void }) {
  const { toast, fail } = useUi();
  const copy = () => void navigator.clipboard?.writeText(text).then(() => toast(t('social.copied')), fail);
  return (
    <div className="soc-hand">
      <Button size="sm" icon="copy" onClick={copy}>
        {t('social.copy_text')}
      </Button>
      {image ? (
        <a className="btn btn-ghost btn-sm" href={image} download>
          <Icon name="download" size={16} />
          <span>{t('social.save_picture')}</span>
        </a>
      ) : null}
      <a className="btn btn-ghost btn-sm" href={OPEN_URL[channel](text)} target="_blank" rel="noopener noreferrer" onClick={channel === 'x' ? undefined : copy}>
        <Icon name="external" size={16} />
        <span>{t('social.open_network', { network: t(`social.ch_${channel}` as MessageKey) })}</span>
      </a>
      <Button size="sm" variant="primary" icon="check" onClick={onMark}>
        {t('social.mark_posted')}
      </Button>
    </div>
  );
}

function MarkPosted({ target, channel, onClose, onDone }: { target: string; channel: Channel; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [url, setUrl] = useState('');
  const bad = url && !/^https:\/\/\S+$/i.test(url.trim());
  return (
    <Dialog
      title={t('social.mark_title', { network: t(`social.ch_${channel}` as MessageKey) })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button onClick={onClose}>{t('social.cancel')}</Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!!bad}
            onClick={() => void run(async () => (await action('social.mark_posted', { id: target, url: url.trim() || null }), await onDone()), t('social.marked'))}
          >
            {t('social.mark_posted')}
          </Button>
        </span>
      }
    >
      <Field label={t('social.post_url')} hint={t('social.post_url_hint')} error={bad ? t('social.e_url') : null}>
        <input className="input" type="url" value={url} placeholder="https://" spellCheck={false} onChange={(e) => setUrl(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

// ---- calendar ------------------------------------------------------------------------------------

function monthStart(v: string | null) {
  const m = v && /^\d{4}-\d{2}$/.test(v) ? v : null;
  const d = m ? new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1, 1) : new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function CalendarTab({ params, set, onOpen, onNew }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void; onNew?: (at: string) => void }) {
  const start = monthStart(params.get('m'));
  // Weeks start on Monday; six rows always fit a month.
  const first = new Date(start);
  first.setDate(1 - ((start.getDay() + 6) % 7));
  const days = Array.from({ length: 42 }, (_, i) => new Date(first.getFullYear(), first.getMonth(), first.getDate() + i));
  const end = days[41]!;
  const range = { from: first.toISOString(), to: new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1).toISOString() };
  const { data } = useAction<PostRow[]>('social.posts', { state: 'all', ...range });
  const byDay = useMemo(() => {
    const m = new Map<string, PostRow[]>();
    for (const p of data ?? []) {
      if (!p.when || p.state === 'cancelled') continue;
      const k = dayKey(new Date(p.when));
      m.set(
        k,
        [...(m.get(k) ?? []), p].sort((a, b) => a.when!.localeCompare(b.when!)),
      );
    }
    return m;
  }, [data]);
  const today = dayKey(new Date());
  const month = (n: number) => {
    const d = new Date(start.getFullYear(), start.getMonth() + n, 1);
    set({ m: `${d.getFullYear()}-${pad(d.getMonth() + 1)}` });
  };
  const weekdays = Array.from({ length: 7 }, (_, i) => new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(days[i]!));
  const newAt = (d: Date) => {
    const at = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 10, 0);
    if (at < new Date()) at.setTime(Date.now() + 3600_000 - (Date.now() % 900_000));
    onNew?.(at.toISOString());
  };
  const agenda = days.filter((d) => d.getMonth() === start.getMonth() && byDay.has(dayKey(d)));
  return (
    <section className="panel soc-cal">
      <div className="soc-cal-head">
        <h2 className="rep-sub">{new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(start)}</h2>
        <div className="soc-cal-nav">
          <IconButton icon="back" label={t('social.prev_month')} onClick={() => month(-1)} />
          <Button size="sm" onClick={() => set({ m: null })}>
            {t('social.today')}
          </Button>
          <IconButton icon="chevronRight" label={t('social.next_month')} onClick={() => month(1)} />
        </div>
      </div>
      {!data ? (
        <Skeleton h={420} />
      ) : (
        <>
          <div className="soc-grid" role="grid" aria-label={t('social.tab_calendar')}>
            {weekdays.map((w) => (
              <span key={w} className="soc-wd" role="columnheader">
                {w}
              </span>
            ))}
            {days.map((d) => {
              const k = dayKey(d);
              const list = byDay.get(k) ?? [];
              const past = k < today;
              return (
                <div key={k} role="gridcell" className={`soc-day${d.getMonth() !== start.getMonth() ? ' other' : ''}${k === today ? ' today' : ''}${past ? ' past' : ''}`}>
                  <span className="soc-day-n">{d.getDate()}</span>
                  {list.slice(0, 3).map((p) => (
                    <button key={p.id} className={`soc-chip soc-chip-${p.state}`} onClick={() => onOpen(p.id)} title={p.excerpt}>
                      <span className="soc-chip-marks">
                        {[...new Set(p.targets.map((x) => x.channel))].map((c) => (
                          <ChannelMark key={c} channel={c} size="sm" />
                        ))}
                      </span>
                      <span className="tabular">{timeOf(p.when!)}</span>
                      <span className="soc-chip-text">{p.excerpt}</span>
                    </button>
                  ))}
                  {list.length > 3 ? <span className="muted small">{t('social.n_more', { n: list.length - 3 })}</span> : null}
                  {onNew && !past ? <IconButton className="soc-day-add" icon="plus" label={t('social.plan_on', { day: d.toLocaleDateString() })} onClick={() => newAt(d)} /> : null}
                </div>
              );
            })}
          </div>
          <ul className="soc-agenda">
            {agenda.length ? (
              agenda.map((d) => (
                <li key={dayKey(d)}>
                  <h3>{new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(d)}</h3>
                  {byDay.get(dayKey(d))!.map((p) => (
                    <button key={p.id} className={`soc-chip soc-chip-${p.state}`} onClick={() => onOpen(p.id)}>
                      <span className="soc-chip-marks">
                        {[...new Set(p.targets.map((x) => x.channel))].map((c) => (
                          <ChannelMark key={c} channel={c} size="sm" />
                        ))}
                      </span>
                      <span className="tabular">{timeOf(p.when!)}</span>
                      <span className="soc-chip-text">{p.excerpt}</span>
                    </button>
                  ))}
                </li>
              ))
            ) : (
              <EmptyState icon="calendar" title={t('social.empty_month')} />
            )}
          </ul>
        </>
      )}
    </section>
  );
}

// ---- one post ------------------------------------------------------------------------------------

function PostDialog({ id, onClose, onChange, onEdit, onOpen }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void; onOpen: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: p, reload } = useAction<Post>('social.post', { id });
  const [marking, setMarking] = useState<Target | null>(null);
  const [numbers, setNumbers] = useState<Target | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const manage = can('social.manage');
  const publish = can('social.publish');
  const done = async () => {
    await reload();
    await onChange();
  };
  const act = (name: string, input: Record<string, unknown>, ok: string, after?: () => Promise<void>) => void run(async () => (await action(name, input), await (after ?? done)()), ok);
  if (!p)
    return (
      <Dialog title={t('social.post')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  const out = p.targets.some((x) => x.state === 'published');
  const left = p.targets.some((x) => ['pending', 'due', 'failed'].includes(x.state));
  const editable = ['draft', 'waiting', 'scheduled'].includes(p.state);
  const total = p.targets.reduce((s, x) => s + (x.likes ?? 0) + (x.comments ?? 0) + (x.shares ?? 0), 0);
  return (
    <Dialog
      title={`${p.number} · ${t(`social.st_${p.state}` as MessageKey)}`}
      onClose={onClose}
      size="lg"
      footer={
        <span className="foot-actions soc-foot">
          {manage && !out && ['draft', 'waiting', 'cancelled', 'failed'].includes(p.state) ? (
            <Button variant="ghost" icon="trash" busy={busy} onClick={() => act('social.delete', { id: p.id }, t('social.deleted'), async () => (onClose(), await onChange()))}>
              {t('social.delete')}
            </Button>
          ) : null}
          {manage ? (
            <Button
              variant="ghost"
              icon="copy"
              busy={busy}
              onClick={() =>
                void run(async () => {
                  const r = await action<{ id: string }>('social.duplicate', { id: p.id });
                  await onChange();
                  onOpen(r.id);
                }, t('social.copied_post'))
              }
            >
              {t('social.duplicate')}
            </Button>
          ) : null}
          {out && p.targets.some((x) => x.mode === 'api' && x.state === 'published') ? (
            <Button variant="ghost" icon="repeat" busy={busy} onClick={() => act('social.refresh_stats', { id: p.id }, t('social.refreshed'))}>
              {t('social.refresh')}
            </Button>
          ) : null}
          {manage && (p.state === 'scheduled' || p.state === 'waiting') ? (
            <Button icon="undo" busy={busy} onClick={() => act('social.cancel', { id: p.id }, t('social.back_to_draft'))}>
              {t('social.unschedule')}
            </Button>
          ) : null}
          {manage && left && ['publishing', 'partly', 'failed'].includes(p.state) ? (
            <Button icon="x" busy={busy} onClick={() => act('social.cancel', { id: p.id }, t('social.stopped'))}>
              {t('social.stop_rest')}
            </Button>
          ) : null}
          {publish && p.state === 'waiting' ? (
            <>
              <Button icon="reply" busy={busy} onClick={() => setRejecting(true)}>
                {t('social.send_back')}
              </Button>
              <Button variant="primary" icon="check" busy={busy} onClick={() => act('social.approve', { id: p.id }, t('social.approved'))}>
                {t('social.approve')}
              </Button>
            </>
          ) : null}
          {manage && editable ? (
            <Button variant={p.state === 'draft' ? 'primary' : 'secondary'} icon="edit" onClick={() => onEdit(p.id)}>
              {p.state === 'draft' ? t('social.edit_and_plan') : t('social.edit')}
            </Button>
          ) : null}
        </span>
      }
    >
      <div className="soc-view">
        {p.note && p.state === 'draft' ? (
          <p className="callout callout-warn">
            <Icon name="reply" size={16} />
            <span>{t('social.sent_back', { note: p.note })}</span>
          </p>
        ) : null}
        {p.last_error && ['publishing', 'partly', 'failed'].includes(p.state) ? (
          <p className="callout callout-warn">
            <Icon name="alert" size={16} />
            <span>{t('social.meta_said', { error: p.last_error })}</span>
          </p>
        ) : null}
        <div className="soc-view-top">
          {p.picture ? <img className="soc-view-img" src={p.picture.url} alt="" /> : null}
          <div className="soc-view-text">
            <p className="soc-body">{p.body || <span className="muted">{t('social.picture_only')}</span>}</p>
            {p.link ? (
              <a className="soc-link" href={p.link} target="_blank" rel="noopener noreferrer">
                <Icon name="link" size={14} /> {p.link}
              </a>
            ) : null}
            <dl className="soc-facts">
              <dt>{p.published_at ? t('social.posted') : t('social.planned')}</dt>
              <dd>{p.published_at ? dateTime(p.published_at) : p.scheduled_at ? dateTime(p.scheduled_at) : t('social.no_time')}</dd>
              <dt>{t('social.written_by')}</dt>
              <dd>{p.author_name ?? '—'}</dd>
              {p.approved_by_name ? (
                <>
                  <dt>{t('social.approved_by')}</dt>
                  <dd>{p.approved_by_name}</dd>
                </>
              ) : null}
              {out ? (
                <>
                  <dt>{t('social.engagement_title')}</dt>
                  <dd className="tabular">{n0(total)}</dd>
                </>
              ) : null}
            </dl>
          </div>
        </div>
        <h3 className="soc-group">{t('social.where')}</h3>
        <ul className="soc-targets">
          {p.targets.map((x) => (
            <li key={x.id}>
              <div className="soc-target-head">
                <ChannelMark channel={x.channel} />
                <span className="vat-party">
                  <strong>{x.account_name}</strong>
                  <span className="muted small">{[x.handle, x.mode === 'api' ? t('social.mode_api') : t('social.mode_manual')].filter(Boolean).join(' · ')}</span>
                </span>
                <TargetState state={x.state} />
              </div>
              {x.own_text ? <p className="soc-own small">{x.text}</p> : null}
              {x.length.over && !['published', 'skipped'].includes(x.state) ? (
                <p className="soc-note warn small">
                  <Icon name="alert" size={14} /> {t('social.too_long', { n: x.length.over })}
                </p>
              ) : null}
              {x.error && x.state !== 'published' ? (
                <p className="soc-note warn small">
                  <Icon name="alert" size={14} /> {x.error}
                </p>
              ) : null}
              {x.state === 'published' ? (
                <div className="soc-target-out">
                  <span className="muted small">
                    {[x.published_at ? dateTime(x.published_at) : null, x.posted_by_name ? t('social.posted_by', { name: x.posted_by_name }) : null].filter(Boolean).join(' · ')}
                  </span>
                  {x.url ? (
                    <a className="link small" href={x.url} target="_blank" rel="noopener noreferrer">
                      {t('social.see_post')} <Icon name="external" size={12} />
                    </a>
                  ) : null}
                  <span className="soc-stats small">
                    <span title={t('social.likes')}>
                      <Icon name="star" size={13} /> {x.likes === null ? '—' : n0(x.likes)}
                    </span>
                    <span title={t('social.comments')}>
                      <Icon name="chat" size={13} /> {x.comments === null ? '—' : n0(x.comments)}
                    </span>
                    <span title={t('social.shares')}>
                      <Icon name="share" size={13} /> {x.shares === null ? '—' : n0(x.shares)}
                    </span>
                    {manage && (x.mode === 'manual' || !x.remote_id) ? (
                      <button className="link" onClick={() => setNumbers(x)}>
                        {t('social.write_numbers')}
                      </button>
                    ) : x.stats_at ? (
                      <span className="muted">{t('social.numbers_at', { at: since(x.stats_at) })}</span>
                    ) : null}
                  </span>
                </div>
              ) : null}
              {manage && (x.state === 'due' || x.state === 'failed') ? (
                <div className="soc-target-acts">
                  {x.state === 'failed' && x.mode === 'api' ? (
                    <Button size="sm" icon="repeat" busy={busy} onClick={() => act('social.retry', { id: x.id }, t('social.published_now'))}>
                      {t('social.try_again')}
                    </Button>
                  ) : null}
                  <HandActions channel={x.channel} text={x.copy} image={p.picture?.url ?? null} onMark={() => setMarking(x)} />
                  <Button size="sm" variant="ghost" icon="minus" busy={busy} onClick={() => act('social.skip', { id: x.id }, t('social.skipped'))}>
                    {t('social.skip')}
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
      {marking ? <MarkPosted target={marking.id} channel={marking.channel} onClose={() => setMarking(null)} onDone={async () => (setMarking(null), await done())} /> : null}
      {numbers ? <NumbersDialog x={numbers} onClose={() => setNumbers(null)} onDone={async () => (setNumbers(null), await done())} /> : null}
      {rejecting ? <RejectDialog id={p.id} onClose={() => setRejecting(false)} onDone={async () => (setRejecting(false), await done())} /> : null}
    </Dialog>
  );
}

function RejectDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [note, setNote] = useState('');
  return (
    <Dialog
      title={t('social.send_back_title')}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button onClick={onClose}>{t('social.cancel')}</Button>
          <Button
            variant="primary"
            icon="reply"
            busy={busy}
            disabled={!note.trim()}
            onClick={() => void run(async () => (await action('social.reject', { id, note: note.trim() }), await onDone()), t('social.sent_back_toast'))}
          >
            {t('social.send_back')}
          </Button>
        </span>
      }
    >
      <Field label={t('social.what_to_change')} required hint={t('social.what_to_change_hint')}>
        <textarea className="input textarea" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-autofocus />
      </Field>
    </Dialog>
  );
}

function NumbersDialog({ x, onClose, onDone }: { x: Target; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [v, setV] = useState({ likes: x.likes, comments: x.comments, shares: x.shares });
  const field = (k: keyof typeof v) => (
    <Field label={t(`social.${k}` as MessageKey)}>
      <input
        className="input tabular"
        inputMode="numeric"
        value={v[k] ?? ''}
        onChange={(e) => {
          const d = e.target.value.replace(/\D/g, '').slice(0, 9);
          setV({ ...v, [k]: d ? Number(d) : null });
        }}
      />
    </Field>
  );
  return (
    <Dialog
      title={t('social.numbers_title', { name: x.account_name })}
      onClose={onClose}
      size="sm"
      footer={
        <span className="foot-actions">
          <Button onClick={onClose}>{t('social.cancel')}</Button>
          <Button variant="primary" icon="check" busy={busy} onClick={() => void run(async () => (await action('social.set_stats', { id: x.id, ...v }), await onDone()), t('social.saved'))}>
            {t('social.save')}
          </Button>
        </span>
      }
    >
      <p className="muted small">{t('social.numbers_hint')}</p>
      <div className="form-grid soc-numbers">
        {field('likes')}
        {field('comments')}
        {field('shares')}
      </div>
    </Dialog>
  );
}

// ---- writing a post ------------------------------------------------------------------------------

function Composer({ id, at, onClose, onDone }: { id: string | null; at: string | null; onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const { fail } = useUi();
  const { data: setup } = useAction<Setup>('social.setup');
  const [postId, setPostId] = useState(id);
  const [loaded, setLoaded] = useState(!id);
  const [body, setBody] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [own, setOwn] = useState<Record<string, string>>({});
  const [ownOpen, setOwnOpen] = useState<string[]>([]);
  const [picture, setPicture] = useState<{ id: string; url: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [link, setLink] = useState('');
  const [when, setWhen] = useState<'now' | 'later'>(at ? 'later' : 'now');
  const [planned, setPlanned] = useState(toLocalInput(at));
  const [preview, setPreview] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const issuesBox = useRef<HTMLUListElement>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (attempt) issuesBox.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [attempt]);
  useEffect(() => {
    if (!id) return;
    void action<Post>('social.post', { id }).then((p) => {
      setBody(p.body);
      setChosen(p.targets.map((x) => x.account));
      const o: Record<string, string> = {};
      for (const x of p.targets) if (x.own_text && x.body !== null) o[x.account] = x.body;
      setOwn(o);
      setOwnOpen(Object.keys(o));
      setPicture(p.picture ? { id: p.picture.id, url: p.picture.url } : null);
      setLink(p.link ?? '');
      if (p.scheduled_at && new Date(p.scheduled_at) > new Date()) {
        setWhen('later');
        setPlanned(toLocalInput(p.scheduled_at));
      }
      setLoaded(true);
    }, fail);
  }, [id]);
  useEffect(() => {
    // A new post goes to every account that can take it; Instagram only once there is a picture.
    if (!id && setup && !chosen.length) setChosen(setup.accounts.filter((a) => !a.problem && a.channel !== 'instagram').map((a) => a.id));
  }, [setup]);
  const accounts = byChannel(setup?.accounts ?? []);
  const picked = accounts.filter((a) => chosen.includes(a.id));
  const textFor = (a: Account) => (ownOpen.includes(a.id) && own[a.id] !== undefined ? own[a.id]! : body);
  const linkOk = !link.trim() || /^https:\/\/\S+$/i.test(link.trim());
  const plannedIso = fromLocalInput(planned);
  const issues: string[] = [];
  if (!picked.length) issues.push(t('social.e_accounts'));
  for (const a of picked) {
    const text = textFor(a).trim();
    const m = measure(a.channel, text, link.trim() || null);
    if (!text && !picture) issues.push(t('social.e_text', { name: a.name }));
    if (m.over) issues.push(t('social.e_long', { name: a.name, n: m.over }));
    if (a.channel === 'instagram' && !picture) issues.push(t('social.e_picture'));
    if (a.channel === 'instagram' && m.tags > IG_TAGS) issues.push(t('social.e_tags', { n: IG_TAGS }));
    if (a.problem) issues.push(t('social.e_connect', { name: a.name }));
  }
  if (!linkOk) issues.push(t('social.e_link'));
  if (when === 'later' && (!plannedIso || new Date(plannedIso) < new Date(Date.now() - 60_000))) issues.push(t('social.e_time'));
  if (picture && picked.some((a) => a.mode === 'api') && !setup?.site_url) issues.push(t('social.e_site'));
  const unique = [...new Set(issues)];
  const toggle = (a: Account) => setChosen(chosen.includes(a.id) ? chosen.filter((x) => x !== a.id) : [...chosen, a.id]);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    try {
      const img = await shrinkPhoto(f, 1440, 0.85);
      const size = await new Promise<{ w: number; h: number }>((resolve) => {
        const i = new Image();
        i.onload = () => resolve({ w: i.naturalWidth, h: i.naturalHeight });
        i.onerror = () => resolve({ w: 0, h: 0 });
        i.src = img.url;
      });
      const r = await action<{ id: string; url: string }>('social.upload_image', { data: img.base64, width: size.w || null, height: size.h || null });
      setPicture({ id: r.id, url: r.url });
    } catch (err) {
      fail(err instanceof Error && err.message === 'not_image' ? new Error(t('social.e_not_image')) : err);
    } finally {
      setUploading(false);
      if (file.current) file.current.value = '';
    }
  };
  const save = async () => {
    const r = await action<{ id: string }>('social.save_post', {
      id: postId,
      body,
      image: picture?.id ?? null,
      link: link.trim() || null,
      accounts: chosen.filter((x) => accounts.some((a) => a.id === x)),
      own: Object.fromEntries(Object.entries(own).filter(([k]) => ownOpen.includes(k) && chosen.includes(k))),
      planned_at: when === 'later' ? plannedIso : null,
    });
    setPostId(r.id);
    return r.id;
  };
  const draft = () => void run(async () => onDone(await save()), t('social.draft_saved'));
  const go = () => {
    setTried(true);
    if (unique.length) return setAttempt((n) => n + 1);
    void run(
      async () => {
        const pid = await save();
        await action('social.submit', { id: pid, now: when === 'now' });
        await onDone(pid);
      },
      setup?.approval ? t('social.sent_for_approval') : when === 'now' ? t('social.posting_now') : t('social.scheduled'),
    );
  };
  const show = picked.find((a) => a.id === preview) ?? picked[0];
  const primary = setup?.approval ? t('social.send_for_approval') : when === 'now' ? t('social.post_now') : t('social.schedule');
  return (
    <Dialog
      title={id ? t('social.edit_post') : t('social.new_post')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <span className="muted small soc-foot-note">{setup?.approval ? t('social.approval_note') : picked.some((a) => a.mode === 'manual') ? t('social.manual_note') : null}</span>
          <span className="foot-actions">
            <Button busy={busy} onClick={draft} disabled={uploading}>
              {t('social.save_draft')}
            </Button>
            <Button variant="primary" icon={setup?.approval ? 'send' : when === 'now' ? 'send' : 'calendar'} busy={busy} disabled={uploading} onClick={go}>
              {primary}
            </Button>
          </span>
        </>
      }
    >
      {!setup || !loaded ? (
        <Skeleton h={420} />
      ) : (
        <div className="soc-editor">
          <div className="form-stack sub-form">
            <fieldset className="soc-pick">
              <legend>{t('social.post_to')}</legend>
              <div className="soc-pick-list">
                {accounts.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className={`soc-pick-acc${chosen.includes(a.id) ? ' on' : ''}${a.problem ? ' warn' : ''}`}
                    aria-pressed={chosen.includes(a.id)}
                    onClick={() => toggle(a)}
                  >
                    <ChannelMark channel={a.channel} />
                    <span className="vat-party">
                      <strong>{a.name}</strong>
                      <span className="small">{a.problem ? t('social.not_connected') : a.mode === 'api' ? t('social.mode_api') : t('social.mode_manual')}</span>
                    </span>
                    <Icon name={chosen.includes(a.id) ? 'checkCircle' : 'circle'} size={18} />
                  </button>
                ))}
              </div>
            </fieldset>
            <Field label={t('social.text')} hint={t('social.text_hint')}>
              <textarea
                className="input textarea soc-text"
                rows={6}
                maxLength={LIMITS.facebook}
                value={body}
                placeholder={t('social.text_example')}
                onChange={(e) => setBody(e.target.value)}
                data-autofocus
              />
            </Field>
            {picked.length ? (
              <ul className="soc-counters" aria-live="polite">
                {picked.map((a) => {
                  const m = measure(a.channel, textFor(a), link.trim() || null);
                  return (
                    <li key={a.id} className={m.over ? 'over' : m.length > m.limit * 0.9 ? 'near' : ''}>
                      <ChannelMark channel={a.channel} size="sm" />
                      <span className="tabular">
                        {n0(m.length)} / {n0(m.limit)}
                      </span>
                      {a.channel === 'instagram' && m.tags ? (
                        <span className="muted">
                          · {m.tags} / {IG_TAGS} #
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          if (ownOpen.includes(a.id)) setOwnOpen(ownOpen.filter((x) => x !== a.id));
                          else {
                            setOwnOpen([...ownOpen, a.id]);
                            if (own[a.id] === undefined) setOwn({ ...own, [a.id]: body });
                          }
                        }}
                      >
                        {ownOpen.includes(a.id) ? t('social.use_same') : t('social.own_text')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {picked
              .filter((a) => ownOpen.includes(a.id))
              .map((a) => (
                <Field key={a.id} label={t('social.text_for', { name: a.name, network: t(`social.ch_${a.channel}` as MessageKey) })}>
                  <textarea className="input textarea" rows={4} maxLength={LIMITS.facebook} value={own[a.id] ?? ''} onChange={(e) => setOwn({ ...own, [a.id]: e.target.value })} />
                </Field>
              ))}
            <div className="soc-media">
              <span className="field-label">{t('social.picture')}</span>
              {picture ? (
                <div className="soc-media-pic">
                  <img src={picture.url} alt="" />
                  <Button size="sm" variant="ghost" icon="trash" onClick={() => setPicture(null)}>
                    {t('social.remove_picture')}
                  </Button>
                </div>
              ) : (
                <Button icon="image" busy={uploading} onClick={() => file.current?.click()}>
                  {t('social.add_picture')}
                </Button>
              )}
              <input ref={file} type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
              <span className="muted small">{t('social.picture_hint')}</span>
            </div>
            <Field label={t('social.link')} hint={t('social.link_hint')} error={tried && !linkOk ? t('social.e_link') : null}>
              <input className="input" type="url" value={link} spellCheck={false} placeholder="https://" onChange={(e) => setLink(e.target.value)} />
            </Field>
            <fieldset className="soc-when">
              <legend>{t('social.when')}</legend>
              <div className="segmented" role="radiogroup" aria-label={t('social.when')}>
                {(['now', 'later'] as const).map((w) => (
                  <button
                    key={w}
                    type="button"
                    role="radio"
                    aria-checked={when === w}
                    className={when === w ? 'active' : ''}
                    onClick={() => {
                      setWhen(w);
                      if (w === 'later' && !planned) setPlanned(toLocalInput(nextHour()));
                    }}
                  >
                    {t(`social.when_${w}` as MessageKey)}
                  </button>
                ))}
              </div>
              {when === 'later' ? (
                <input
                  className="input"
                  type="datetime-local"
                  value={planned}
                  aria-label={t('social.when_later')}
                  min={toLocalInput(new Date().toISOString())}
                  onChange={(e) => setPlanned(e.target.value)}
                />
              ) : null}
            </fieldset>
            {tried && unique.length ? (
              <ul ref={issuesBox} className="soc-issues" role="alert">
                {unique.map((x) => (
                  <li key={x}>
                    <Icon name="alert" size={14} /> {x}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <div className="soc-preview">
            {show ? (
              <>
                {picked.length > 1 ? (
                  <div className="segmented soc-preview-tabs" role="tablist" aria-label={t('social.preview')}>
                    {picked.map((a) => (
                      <button key={a.id} role="tab" aria-selected={show.id === a.id} className={show.id === a.id ? 'active' : ''} onClick={() => setPreview(a.id)}>
                        <ChannelMark channel={a.channel} size="sm" />
                        <span className="sr-only">{a.name}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
                <PreviewCard account={show} text={textFor(show)} picture={picture?.url ?? null} link={link.trim() || null} />
                <p className="muted small">{t('social.preview_note')}</p>
              </>
            ) : (
              <EmptyState icon="eye" title={t('social.preview_empty')} />
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** How the post will roughly look: name, text, picture, link. Not a copy of any network's design. */
function PreviewCard({ account, text, picture, link }: { account: Account; text: string; picture: string | null; link: string | null }) {
  const shown = account.channel === 'facebook' ? text : copyText(account.channel, text, link);
  const host = link ? link.replace(/^https?:\/\//, '').split('/')[0] : null;
  return (
    <article className={`soc-card soc-card-${account.channel}`} aria-label={t('social.preview')}>
      <header>
        <ChannelMark channel={account.channel} />
        <span className="vat-party">
          <strong>{account.name}</strong>
          <span className="muted small">{account.handle ?? t(`social.ch_${account.channel}` as MessageKey)}</span>
        </span>
      </header>
      {account.channel === 'instagram' && picture ? <img src={picture} alt="" /> : null}
      {shown ? <p className="soc-card-text">{shown}</p> : null}
      {account.channel !== 'instagram' && picture ? <img src={picture} alt="" /> : null}
      {account.channel === 'facebook' && link && !picture ? (
        <div className="soc-card-link">
          <Icon name="link" size={14} />
          <span>{host}</span>
        </div>
      ) : null}
      {account.channel === 'instagram' && !picture ? <p className="soc-note warn small">{t('social.e_picture')}</p> : null}
    </article>
  );
}

// ---- accounts ------------------------------------------------------------------------------------

function AccountsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { toast, fail } = useUi();
  const { data, reload } = useAction<Account[]>('social.accounts');
  const [edit, setEdit] = useState<Account | 'new' | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const settings = can('social.settings');
  const done = async () => {
    await reload();
    await onChange();
  };
  const verify = async (a: Account) => {
    setChecking(a.id);
    try {
      const r = await action<{ ok: boolean; name?: string; error?: string }>('social.verify_account', { id: a.id });
      if (r.ok) toast(t('social.connected_toast', { name: r.name ?? '' }));
      else fail(new Error(r.error ?? ''));
      await done();
    } catch (err) {
      fail(err);
    } finally {
      setChecking(null);
    }
  };
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('social.accounts')}</h2>
          <p className="muted small">{t('social.accounts_hint')}</p>
        </div>
        {settings ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('social.add_account')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={200} />
      ) : data.length ? (
        <ul className="soc-accounts">
          {byChannel(data).map((a) => (
            <li key={a.id} className={a.active ? '' : 'inactive'}>
              <div className="soc-acc-head">
                <ChannelMark channel={a.channel} />
                <span className="vat-party">
                  <strong>{a.name}</strong>
                  <span className="muted small">{a.handle ?? t(`social.ch_${a.channel}` as MessageKey)}</span>
                </span>
              </div>
              <p className={`soc-acc-state small${a.problem || a.last_error ? ' warn' : a.mode === 'api' && a.verified_at ? ' ok' : ''}`}>
                <Icon name={!a.active ? 'minus' : a.problem || a.last_error ? 'alert' : a.mode === 'api' ? (a.verified_at ? 'checkCircle' : 'circle') : 'edit'} size={14} />
                {!a.active
                  ? t('social.acc_off')
                  : a.last_error
                    ? t('social.acc_error', { error: a.last_error })
                    : a.problem
                      ? t(`social.p_${a.problem}` as MessageKey)
                      : a.mode === 'manual'
                        ? t('social.acc_manual')
                        : a.verified_at
                          ? t('social.acc_connected', { name: a.verified_name ?? a.name, at: dateTime(a.verified_at) })
                          : t('social.acc_not_checked')}
              </p>
              <span className="muted small">{[tn('social.n_posted', a.published ?? 0), a.last_post ? t('social.last_post', { at: since(a.last_post) }) : null].filter(Boolean).join(' · ')}</span>
              {settings ? (
                <div className="row-actions">
                  <Button size="sm" variant="ghost" icon="edit" onClick={() => setEdit(a)}>
                    {t('social.edit')}
                  </Button>
                  {a.mode === 'api' && a.active && a.has_token ? (
                    <Button size="sm" variant="ghost" icon="shieldCheck" busy={checking === a.id} onClick={() => void verify(a)}>
                      {t('social.check')}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="share"
          title={t('social.no_accounts_title')}
          body={t('social.no_accounts_hint')}
          action={
            settings ? (
              <Button variant="primary" icon="plus" onClick={() => setEdit('new')}>
                {t('social.add_account')}
              </Button>
            ) : undefined
          }
        />
      )}
      {edit ? (
        <AccountDialog
          a={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async (saved) => {
            setEdit(null);
            await done();
            if (saved.mode === 'api' && saved.has_token && !saved.verified_at && saved.active) await verify(saved);
          }}
        />
      ) : null}
    </section>
  );
}

function AccountDialog({ a, onClose, onSaved }: { a: Account | null; onClose: () => void; onSaved: (a: Account) => Promise<void> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const { data: s } = useAction<Settings>('social.settings');
  const [channel, setChannel] = useState<Channel>(a?.channel ?? 'facebook');
  const [name, setName] = useState(a?.name ?? '');
  const [handle, setHandle] = useState(a?.handle ?? '');
  const [external, setExternal] = useState(a?.external_id ?? '');
  const [token, setToken] = useState('');
  const [active, setActive] = useState(a?.active ?? true);
  const [tried, setTried] = useState(false);
  const api = API.includes(channel);
  const errors = {
    name: !name.trim() ? t('social.e_name') : null,
    external: api && external && !/^\d{5,30}$/.test(external) ? t('social.e_id') : null,
    token: api && token && !/^[A-Za-z0-9_\-|.]{20,1000}$/.test(token) ? t('social.e_token') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      const out = await action<Account>('social.save_account', {
        id: a?.id ?? null,
        channel,
        name: name.trim(),
        handle: handle.trim() || null,
        external_id: api ? external || null : null,
        ...(api && token ? { token } : {}),
        active,
      });
      await onSaved(out);
    }, t('social.account_saved'));
  };
  const remove = () =>
    void run(async () => {
      const r = await action<{ kept: boolean }>('social.delete_account', { id: a!.id });
      if (r.kept) toast(t('social.account_kept'));
      await onSaved({ ...a!, active: false, mode: 'manual' });
    }, t('social.account_removed'));
  const err = (e: string | null) => (tried ? e : null);
  return (
    <Dialog
      title={a ? t('social.edit_account') : t('social.add_account')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {a ? (
            <Button variant="ghost" icon="trash" busy={busy} onClick={remove}>
              {t('social.remove_account')}
            </Button>
          ) : (
            <span />
          )}
          <span className="foot-actions">
            <Button onClick={onClose}>{t('social.cancel')}</Button>
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {t('social.save')}
            </Button>
          </span>
        </>
      }
    >
      <div className="form-stack">
        <fieldset className="soc-channels">
          <legend>{t('social.network')}</legend>
          <div className="soc-channel-list" role="radiogroup" aria-label={t('social.network')}>
            {CHANNELS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={channel === c}
                className={`soc-channel${channel === c ? ' on' : ''}`}
                onClick={() => setChannel(c)}
                disabled={!!a && a.channel !== c && (a.published ?? 0) > 0}
              >
                <ChannelMark channel={c} />
                <span className="vat-party">
                  <strong>{t(`social.ch_${c}` as MessageKey)}</strong>
                  <span className="muted small">{API.includes(c) ? t('social.mode_api') : t('social.mode_manual')}</span>
                </span>
              </button>
            ))}
          </div>
        </fieldset>
        <div className="form-grid">
          <Field label={t('social.acc_name')} required error={err(errors.name)} hint={t('social.acc_name_hint')}>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('social.handle')} hint={t('social.handle_hint')}>
            <input
              className="input"
              value={handle}
              maxLength={80}
              spellCheck={false}
              placeholder={channel === 'facebook' ? 'facebook.com/…' : channel === 'linkedin' ? 'linkedin.com/company/…' : '@…'}
              onChange={(e) => setHandle(e.target.value)}
            />
          </Field>
        </div>
        {api ? (
          <section className="panel form-stack sub-form soc-connect">
            <div>
              <h3 className="rep-sub">{t('social.connect')}</h3>
              <ol className="soc-steps small">
                <li>{channel === 'facebook' ? t('social.step_fb_1') : t('social.step_ig_1')}</li>
                <li>{t('social.step_2')}</li>
                <li>{t('social.step_3')}</li>
              </ol>
            </div>
            {s && !s.can_keep_token ? (
              <p className="callout callout-warn">
                <Icon name="alert" size={16} />
                <span>{t('social.no_key')}</span>
              </p>
            ) : null}
            <div className="form-grid">
              <Field
                label={channel === 'facebook' ? t('social.page_id') : t('social.ig_id')}
                error={err(errors.external)}
                hint={channel === 'facebook' ? t('social.page_id_hint') : t('social.ig_id_hint')}
              >
                <input className="input tabular" inputMode="numeric" value={external} spellCheck={false} autoComplete="off" onChange={(e) => setExternal(e.target.value.replace(/\s/g, ''))} />
              </Field>
              <Field label={t('social.token')} error={err(errors.token)} hint={a?.has_token && !token ? t('social.token_kept') : t('social.token_hint')}>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={token}
                  placeholder={a?.has_token ? '••••••••' : ''}
                  disabled={!!s && !s.can_keep_token}
                  onChange={(e) => setToken(e.target.value.trim())}
                />
              </Field>
            </div>
          </section>
        ) : (
          <p className="callout callout-info">
            <Icon name="info" size={16} />
            <span>{t('social.manual_explained', { network: t(`social.ch_${channel}` as MessageKey) })}</span>
          </p>
        )}
        {a ? (
          <label className="setting-row soc-row">
            <span>
              <strong>{t('social.active')}</strong>
              <span className="muted small">{t('social.active_hint')}</span>
            </span>
            <Switch checked={active} onChange={setActive} label={t('social.active')} />
          </label>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- settings ------------------------------------------------------------------------------------

function SettingsTab({ onChange }: { onChange: () => Promise<void> }) {
  const { busy, run } = useSave();
  const { data, reload } = useAction<Settings>('social.settings');
  const [f, setF] = useState<Settings | null>(null);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (data && !f) setF({ ...data, site_url: data.site_url ?? (window.location.protocol === 'https:' ? window.location.origin : null) });
  }, [data]);
  if (!f) return <Skeleton h={300} />;
  const bad = f.site_url && !/^https:\/\/[a-z0-9.-]+(:\d+)?(\/[\w./-]*)?$/i.test(f.site_url) ? t('social.e_site_url') : null;
  const save = () => {
    setTried(true);
    if (bad) return;
    void run(async () => {
      await action('social.save_settings', { approval: f.approval, site_url: f.site_url || null });
      await reload();
      await onChange();
    }, t('social.settings_saved'));
  };
  return (
    <div className="form-stack">
      <section className="panel form-stack sub-form">
        <label className="setting-row soc-row">
          <span>
            <strong>{t('social.approval')}</strong>
            <span className="muted small">{t('social.approval_hint')}</span>
          </span>
          <Switch checked={f.approval} onChange={(v) => setF({ ...f, approval: v })} label={t('social.approval')} />
        </label>
      </section>
      <section className="panel form-stack sub-form">
        <div>
          <h2 className="rep-sub">{t('social.pictures')}</h2>
          <p className="muted small">{t('social.pictures_hint')}</p>
        </div>
        <Field label={t('social.site_url')} error={tried ? bad : null} hint={t('social.site_url_hint')}>
          <input className="input" value={f.site_url ?? ''} spellCheck={false} placeholder="https://" onChange={(e) => setF({ ...f, site_url: e.target.value.trim().replace(/\/+$/, '') || null })} />
        </Field>
      </section>
      <div className="form-actions">
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('social.save')}
        </Button>
      </div>
    </div>
  );
}
